/**
 * MyCorrectionsSection — Feb 28, 2026
 *
 * Provider Settings section that lists "Fixes I always make" — repeated
 * word substitutions the detector finds in your last 30 days of edited
 * notes. Approve → joins your `prompt_addendum`, so the LLM will honor
 * the substitution on every future generate. Dismiss → never suggest
 * again. Re-detect → manually re-run the diff scanner.
 *
 * Wire shape:
 *   GET /api/me/corrections?status=suggested|approved|dismissed
 *   POST /api/me/corrections/detect
 *   POST /api/me/corrections/approve {from_word, to_word}
 *   POST /api/me/corrections/dismiss {from_word, to_word}
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Check, X, RefreshCw, ArrowRight, Loader2, BookOpen,
} from 'lucide-react';

const API = window.__WR_API_BASE__;

const TABS = [
  { id: 'suggested', label: 'Suggested',  empty: 'No suggestions yet. Click "Re-scan" to look at your last 30 days of edited notes.' },
  { id: 'approved',  label: 'Approved',   empty: 'No approved patterns yet. Approve a suggestion and it will be honored on every future Generate.' },
  { id: 'dismissed', label: 'Dismissed',  empty: 'No dismissed patterns.' },
];

const Row = ({ item, onApprove, onDismiss, busy }) => {
  const { from_word, to_word, occurrences, status, last_seen_at } = item;
  const isPending = busy === `${from_word}__${to_word}`;
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded border ${
        status === 'approved'
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : status === 'dismissed'
            ? 'bg-secondary/40 border-border opacity-70'
            : 'bg-amber-500/[0.04] border-amber-500/30'
      }`}
      data-testid={`correction-row-${from_word}-${to_word}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-mono px-1 rounded bg-rose-500/15 text-rose-200">
            {from_word}
          </span>
          <ArrowRight className="w-3 h-3 text-muted-foreground" />
          <span className="font-mono px-1 rounded bg-emerald-500/15 text-emerald-200">
            {to_word}
          </span>
          <span className="ml-2 text-[10px] text-muted-foreground">
            seen in <strong>{occurrences}</strong> note{occurrences === 1 ? '' : 's'}
          </span>
        </div>
        {last_seen_at && (
          <div className="text-[9px] text-muted-foreground/70 mt-0.5">
            Last seen: {new Date(last_seen_at).toLocaleDateString()}
          </div>
        )}
      </div>
      {status !== 'approved' && (
        <button
          type="button"
          onClick={() => onApprove(item)}
          disabled={isPending}
          className="h-6 w-6 inline-flex items-center justify-center rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
          title="Approve — the LLM will honor this substitution on every Generate"
          data-testid={`correction-approve-${from_word}-${to_word}`}
        >
          {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
      )}
      {status !== 'dismissed' && (
        <button
          type="button"
          onClick={() => onDismiss(item)}
          disabled={isPending}
          className="h-6 w-6 inline-flex items-center justify-center rounded border border-border text-muted-foreground hover:bg-rose-500/15 hover:text-rose-300 hover:border-rose-500/40 disabled:opacity-50 transition-colors"
          title="Dismiss — never suggest this again"
          data-testid={`correction-dismiss-${from_word}-${to_word}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export const MyCorrectionsSection = () => {
  const [tab, setTab] = useState('suggested');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (status) => {
    if (!API) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/me/corrections`, {
        params: status ? { status } : {},
        withCredentials: true,
      });
      setItems(r.data?.corrections || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed';
      toast.error(`Corrections load failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const handleApprove = async (item) => {
    const key = `${item.from_word}__${item.to_word}`;
    setBusy(key);
    try {
      await axios.post(`${API}/api/me/corrections/approve`, {
        from_word: item.from_word, to_word: item.to_word,
      }, { withCredentials: true });
      toast.success(`Approved — LLM will write '${item.to_word}' instead of '${item.from_word}'`);
      load(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally { setBusy(null); }
  };

  const handleDismiss = async (item) => {
    const key = `${item.from_word}__${item.to_word}`;
    setBusy(key);
    try {
      await axios.post(`${API}/api/me/corrections/dismiss`, {
        from_word: item.from_word, to_word: item.to_word,
      }, { withCredentials: true });
      toast.success(`Dismissed: ${item.from_word} → ${item.to_word}`);
      load(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dismiss failed');
    } finally { setBusy(null); }
  };

  const handleRescan = async () => {
    if (!API) return;
    setScanning(true);
    try {
      const r = await axios.post(`${API}/api/me/corrections/detect`, null, {
        params: { lookback_days: 30 },
        withCredentials: true,
      });
      const found = r.data?.patterns_upserted || 0;
      const scanned = r.data?.scanned_notes || 0;
      toast.success(`Scanned ${scanned} note${scanned === 1 ? '' : 's'} — ${found} pattern${found === 1 ? '' : 's'} updated`);
      load(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const emptyHint = TABS.find((t) => t.id === tab)?.empty;

  return (
    <div className="rounded-lg border border-purple-500/30 bg-purple-500/[0.04] p-3 mt-3" data-testid="my-corrections-section">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-bold text-purple-200 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Fixes I Always Make
            <span className="text-[9px] px-1 rounded bg-purple-500/30 text-purple-100 font-bold">BETA</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            WriteRight watches the edits you make to LLM output and learns
            your style. Approve a pattern below and the LLM will write it
            your way on every future Generate.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRescan}
          disabled={scanning}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-purple-500/40 text-purple-300 hover:bg-purple-500/15 text-[10px] font-semibold transition-colors disabled:opacity-50"
          data-testid="corrections-rescan-btn"
          title="Re-scan your last 30 days of edited notes"
        >
          {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Re-scan
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-2 border-b border-border/40">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              tab === t.id
                ? 'text-purple-200 border-b-2 border-purple-400/80'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`corrections-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-[10px] text-muted-foreground">
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <span><strong>{items.length}</strong> item{items.length === 1 ? '' : 's'}</span>
          )}
        </div>
      </div>

      {/* Body */}
      {items.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic flex items-start gap-2 px-2 py-3 rounded bg-secondary/20">
          <BookOpen className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-70" />
          <span>{emptyHint}</span>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {items.map((item) => (
            <Row
              key={`${item.from_word}__${item.to_word}`}
              item={item}
              onApprove={handleApprove}
              onDismiss={handleDismiss}
              busy={busy}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCorrectionsSection;
